
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styles from './styles.module.css';
import { getBusinessByLogin } from '../../../../services/business';
import { SocialMediaAuthPopover } from './SocialMediaAuthPopover';
import {
  Facebook,
  Instagram,
  Linkedin,
  LockClosed,
  TikTok,
  XTwitter,
} from '../../../../assets/svgs/icons';
import { getLinkedinByBusiness } from '../../../../services/linkedinService';
import {
  getTiktokLoginByBusiness,
  getTiktokCreatorInfo,
} from '../../../../services/tiktokService';
import {
  getFacebookLoginByBusiness,
  getFacebookPages,
} from '../../../../services/metaService';
import { getTwitterByBusiness } from '../../../../services/twitterService';
import { getSocialMediaConnections } from '../../../../services/socialMediaConnectionService';
import {
  notifyErrorCustom,
  notifyWarningCustom,
} from '../../../../utils/notify';
import { useChatSocketInstagramOAuth } from '../../hooks/useChatSocketInstagramOAuth';

const SOCIAL_NETWORKS = [
  {
    id: 4,
    key: 'instagram',
    name: 'Instagram',
    icon: <Instagram width={24} height={24} color="currentColor" />,
  },
  {
    id: 5,
    key: 'facebook',
    name: 'Facebook',
    icon: <Facebook width={24} height={24} color="currentColor" />,
  },
  {
    id: 1,
    key: 'linkedin',
    name: 'LinkedIn',
    icon: <Linkedin width={24} height={24} color="currentColor" />,
  },
  {
    id: 2,
    key: 'twitter',
    name: 'Twitter/X',
    icon: <XTwitter width={24} height={24} color="currentColor" />,
  },
  {
    id: 3,
    key: 'tiktok',
    name: 'TikTok',
    icon: <TikTok width={24} height={24} color="currentColor" />,
  },
];

const NETWORK_NAME_KEYS = {
  instagram: 'chatsocket.social_media.networks.instagram',
  facebook: 'chatsocket.social_media.networks.facebook',
  linkedin: 'chatsocket.social_media.networks.linkedin',
  twitter: 'chatsocket.social_media.networks.twitter',
  tiktok: 'chatsocket.social_media.networks.tiktok',
};

const OAUTH_POLL_INTERVAL_MS = 500;
const OAUTH_CLOSE_DELAY_MS = 8000;
const SOCIAL_REDIRECT_PATH = '/SocialMediaRedirect';

const DEFAULT_NETWORK_SETTINGS = {
  instagram: {
    instagramItems: [],
  },
  facebook: {
    facebookPageToken: null,
    facebookItems: [],
  },
  tiktok: {
    headline: '',
    privacyLevel: '',
    comment: false,
    duet: false,
    stitch: false,
    music: false,
    musicConfirmation: false,
    contentDisclosure: false,
    yourBrand: false,
    brandedContent: false,
  },
  linkedin: {},
  twitter: {},
};

const resolveInitialSelectedNetworkIds = (initialSelection = []) => {
  if (!Array.isArray(initialSelection)) {
    return [];
  }

  const ids = initialSelection
    .map((item) => {
      if (typeof item === 'number') {
        return item;
      }

      if (typeof item === 'string') {
        const byKey = SOCIAL_NETWORKS.find((network) => network.key === item);
        return byKey?.id ?? null;
      }

      if (!item || typeof item !== 'object') {
        return null;
      }

      if (typeof item.typeId === 'number') {
        return item.typeId;
      }

      if (typeof item.id === 'number') {
        return item.id;
      }

      const key = item.name || item.key;
      if (typeof key === 'string') {
        const byKey = SOCIAL_NETWORKS.find((network) => network.key === key);
        return byKey?.id ?? null;
      }

      return null;
    })
    .filter((id) => Number.isFinite(id));

  return [...new Set(ids)];
};

const resolveInitialNetworkSettings = (initialSelection = []) => {
  if (!Array.isArray(initialSelection)) {
    return DEFAULT_NETWORK_SETTINGS;
  }

  const nextSettings = {
    ...DEFAULT_NETWORK_SETTINGS,
  };

  initialSelection.forEach((item) => {
    if (!item || typeof item !== 'object') {
      return;
    }

    const key = item.name || item.key;
    if (!key || typeof key !== 'string' || !item.data || typeof item.data !== 'object') {
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(nextSettings, key)) {
      return;
    }

    nextSettings[key] = {
      ...(nextSettings[key] || {}),
      ...item.data,
    };
  });

  return nextSettings;
};

export const SocialMediaSelector = ({
  campaignArtGenerationId = null,
  onFinalize,
  isReadOnly = false,
  initialSelection = [],
}) => {
  const { t } = useTranslation();
  const [businessId, setBusinessId] = useState(null);
  const [connections, setConnections] = useState({});
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedNetworks, setSelectedNetworks] = useState([]);
  const [authPopoverNetwork, setAuthPopoverNetwork] = useState(null);
  const [networkSettings, setNetworkSettings] = useState(
    DEFAULT_NETWORK_SETTINGS,
  );
  const [facebookPages, setFacebookPages] = useState([]);
  const [tiktokCreatorInfo, setTiktokCreatorInfo] = useState(null);
  const [tiktokPrivacyOptions, setTiktokPrivacyOptions] = useState([]);

  const instagramNetwork = SOCIAL_NETWORKS.find((item) => item.key === 'instagram');

  const oauthIntervalRef = useRef(null);
  const oauthTimeoutRef = useRef(null);
  const location = useLocation();

  const clearOAuthWatchers = useCallback(() => {
    if (oauthIntervalRef.current) {
      clearInterval(oauthIntervalRef.current);
      oauthIntervalRef.current = null;
    }

    if (oauthTimeoutRef.current) {
      clearTimeout(oauthTimeoutRef.current);
      oauthTimeoutRef.current = null;
    }
  }, []);

  const loadConnections = useCallback(
    async (targetBusinessId, options = {}) => {
      const { silent = false } = options;

      if (!targetBusinessId) {
        setConnections({});
        if (!silent) {
          setLoadingConnections(false);
        }
        return {};
      }

      if (!silent) {
        setLoadingConnections(true);
      }

      try {
        const response = await getSocialMediaConnections(targetBusinessId);
        const nextConnections = response?.data || {};
        setConnections(nextConnections);
        return nextConnections;
      } catch (error) {
        console.error('Error loading social media connections', error);
        setConnections({});
        return {};
      } finally {
        if (!silent) {
          setLoadingConnections(false);
        }
      }
    },
    [],
  );

  const syncConnectionAndSelection = useCallback(
    async (network) => {
      const latestConnections = await loadConnections(businessId, {
        silent: true,
      });
      const connected = !!latestConnections?.[network.key];

      if (connected) {
        setSelectedNetworks((previous) =>
          previous.includes(network.id) ? previous : [...previous, network.id],
        );
      }

      return connected;
    },
    [businessId, loadConnections],
  );

  const updateNetworkSettings = useCallback((networkKey, updater) => {
    setNetworkSettings((previous) => {
      const current = previous?.[networkKey] || {};
      const nextPartial =
        typeof updater === 'function' ? updater(current) : updater;
      const nextData = { ...current, ...nextPartial };
      return { ...previous, [networkKey]: nextData };
    });
  }, []);

  const ensureFacebookPages = useCallback(async () => {
    if (!businessId) return;
    try {
      const response = await getFacebookPages(businessId);
      if (
        response?.responseStatus === 200 &&
        Array.isArray(response?.data?.data)
      ) {
        const pages = response.data.data.map((page) => ({
          label: page.name,
          value: page.access_token,
          id: page.id,
        }));
        setFacebookPages(pages);
      }
    } catch (error) {
      console.error('Error loading Facebook pages', error);
      setFacebookPages([]);
    }
  }, [businessId]);

  const ensureTiktokCreatorInfo = useCallback(async () => {
    if (!businessId) return;
    try {
      const response = await getTiktokCreatorInfo(businessId);
      if (response?.dataT?.error?.code !== 'ok') {
        return;
      }
      const creatorInfo = response.dataT.data;
      setTiktokCreatorInfo(creatorInfo);
      if (Array.isArray(creatorInfo?.privacy_level_options)) {
        setTiktokPrivacyOptions(
          creatorInfo.privacy_level_options.map((item) => ({
            label: item,
            value: item,
          })),
        );
      }
    } catch (error) {
      console.error('Error loading TikTok creator info', error);
    }
  }, [businessId]);

  const openPostAuthPopover = useCallback(
    async (network) => {
      if (isReadOnly) return;

      setSelectedNetworks((previous) =>
        previous.includes(network.id) ? previous : [...previous, network.id],
      );

      setAuthPopoverNetwork(network);

      if (network.key === 'facebook') {
        ensureFacebookPages();
      }

      if (network.key === 'tiktok') {
        ensureTiktokCreatorInfo();
      }
    },
    [ensureFacebookPages, ensureTiktokCreatorInfo, isReadOnly],
  );

  const {
    startInstagramOAuth,
    clearInstagramOAuthWatchers,
    cancelInstagramOAuth,
    reopenInstagramOAuthWindow,
    usesInstagramOAuthRedirect,
  } = useChatSocketInstagramOAuth({
    businessId,
    campaignArtGenerationId,
    instagramNetwork,
    loadConnections,
    setBusinessId,
    setSelectedNetworks,
    openPostAuthPopover,
    setIsConnecting,
  });

  useEffect(() => {
    return () => {
      clearOAuthWatchers();
      clearInstagramOAuthWatchers();
    };
  }, [clearOAuthWatchers, clearInstagramOAuthWatchers]);

  useEffect(() => {
    const fetchBusinessAndConnections = async () => {
      let nextBusinessId = null;

      if (location.state?.instagramOAuth?.businessId) {
        nextBusinessId = location.state.instagramOAuth.businessId;
      } else if (location.state?.queryBusinessId) {
        nextBusinessId = location.state.queryBusinessId;
      } else {
        const businessData = await getBusinessByLogin();
        nextBusinessId = businessData?.data?.[0]?.uuid || null;
      }

      setBusinessId(nextBusinessId);
      await loadConnections(nextBusinessId);
    };

    fetchBusinessAndConnections();
  }, [location.state, loadConnections]);

  useEffect(() => {
    setSelectedNetworks((previous) =>
      previous.filter((networkId) => {
        const network = SOCIAL_NETWORKS.find((item) => item.id === networkId);
        return network ? !!connections?.[network.key] : false;
      }),
    );
  }, [connections]);

  useEffect(() => {
    const hydratedIds = resolveInitialSelectedNetworkIds(initialSelection);
    const hydratedSettings = resolveInitialNetworkSettings(initialSelection);

    setSelectedNetworks(hydratedIds);
    setNetworkSettings((previous) => ({
      ...previous,
      ...hydratedSettings,
    }));
  }, [initialSelection]);

  const watchOAuthWindow = useCallback(
    (oauthWindow, network, redirectPathMarker) => {
      clearOAuthWatchers();

      oauthIntervalRef.current = setInterval(async () => {
        if (oauthWindow.closed) {
          clearOAuthWatchers();
          const connected = await syncConnectionAndSelection(network);
          setIsConnecting(false);
          if (connected) {
            await openPostAuthPopover(network);
          }
          return;
        }

        try {
          if (oauthWindow.location.href.includes(redirectPathMarker)) {
            clearOAuthWatchers();
            oauthTimeoutRef.current = setTimeout(async () => {
              if (!oauthWindow.closed) {
                oauthWindow.close();
              }

              const connected = await syncConnectionAndSelection(network);
              setIsConnecting(false);
              if (connected) {
                await openPostAuthPopover(network);
              }
            }, OAUTH_CLOSE_DELAY_MS);
          }
        } catch (error) {
          // While in external OAuth domains this access may throw; polling continues.
        }
      }, OAUTH_POLL_INTERVAL_MS);
    },
    [clearOAuthWatchers, openPostAuthPopover, syncConnectionAndSelection],
  );

  const handleConnect = async (network) => {
    if (isReadOnly) {
      return;
    }

    if (!businessId) {
      return;
    }

    if (network.key === 'instagram') {
      startInstagramOAuth();
      return;
    }

    setIsConnecting(true);

    try {
      let authUrl = null;
      let redirectMarker = 'SocialMediaRedirect';

      if (network.key === 'linkedin') {
        const response = await getLinkedinByBusiness(businessId);
        authUrl = response?.dataL;
        redirectMarker = 'LinkedinRedirection';
      }

      if (network.key === 'twitter') {
        const response = await getTwitterByBusiness(businessId);
        authUrl = response?.dataT?.authUrl || response?.dataT;
      }

      if (network.key === 'tiktok') {
        const response = await getTiktokLoginByBusiness(businessId);
        authUrl = response?.dataT;
      }

      if (network.key === 'facebook') {
        const response = await getFacebookLoginByBusiness(businessId);
        authUrl = response?.dataT;
      }

      if (!authUrl) {
        setIsConnecting(false);
        return;
      }

      const oauthWindow = window.open(
        authUrl,
        '_blank',
        'width=500,height=600',
      );
      if (!oauthWindow) {
        setIsConnecting(false);
        return;
      }

      watchOAuthWindow(oauthWindow, network, redirectMarker);
    } catch (error) {
      console.error('Error starting OAuth setup', error);
      setIsConnecting(false);
    }
  };

  const handleNetworkClick = (network) => {
    if (isReadOnly || loadingConnections || isConnecting) {
      return;
    }

    if (!connections?.[network.key]) {
      handleConnect(network);
      return;
    }

    openPostAuthPopover(network);
  };

  const handleSaveNetworkSettings = () => {
    if (isReadOnly) {
      return;
    }

    setAuthPopoverNetwork(null);
  };

  const isNetworkSelected = (key) => {
    const network = SOCIAL_NETWORKS.find((item) => item.key === key);
    return network ? selectedNetworks.includes(network.id) : false;
  };

  const validateBeforeFinalize = () => {
    if (isReadOnly) {
      return false;
    }

    const tiktokSelected = isNetworkSelected('tiktok');
    const instagramSelected = isNetworkSelected('instagram');
    const facebookSelected = isNetworkSelected('facebook');

    if (tiktokSelected) {
      const tiktokSettings = networkSettings?.tiktok || {};

      if (!tiktokSettings?.privacyLevel) {
        notifyErrorCustom(t('chatsocket.social_media.errors.tiktok_privacy'));
        return false;
      }

      if (!tiktokSettings?.headline || !tiktokSettings.headline.trim()) {
        notifyErrorCustom(t('chatsocket.social_media.errors.tiktok_title'));
        return false;
      }

      if (tiktokSettings?.music && !tiktokSettings?.musicConfirmation) {
        notifyErrorCustom(t('chatsocket.social_media.errors.tiktok_music'));
        return false;
      }

      if (
        tiktokSettings?.contentDisclosure &&
        !tiktokSettings?.yourBrand &&
        !tiktokSettings?.brandedContent
      ) {
        notifyWarningCustom(
          t('chatsocket.social_media.errors.tiktok_sponsored'),
        );
        return false;
      }
    }

    if (instagramSelected) {
      const instagramItems = networkSettings?.instagram?.instagramItems || [];
      if (!instagramItems.length) {
        notifyErrorCustom(t('chatsocket.social_media.errors.instagram_type'));
        return false;
      }
    }

    if (facebookSelected) {
      const facebookSettings = networkSettings?.facebook || {};
      if (!facebookSettings?.facebookPageToken?.value) {
        notifyErrorCustom(t('chatsocket.social_media.errors.facebook_page'));
        return false;
      }
      const facebookItems = facebookSettings?.facebookItems || [];
      if (!facebookItems.length) {
        notifyErrorCustom(t('chatsocket.social_media.errors.facebook_type'));
        return false;
      }
    }

    return true;
  };

  const buildNetworkPayload = () => {
    return selectedNetworks
      .map((networkId) => {
        const network = SOCIAL_NETWORKS.find((item) => item.id === networkId);
        if (!network) {
          return null;
        }

        return {
          typeId: network.id,
          name: network.key,
          data: networkSettings?.[network.key] || {},
        };
      })
      .filter(Boolean);
  };

  const handleDeselectNetwork = (event, network) => {
    event.stopPropagation();
    if (isReadOnly) {
      return;
    }

    setSelectedNetworks((previous) =>
      previous.filter((id) => id !== network.id),
    );
    if (authPopoverNetwork?.id === network.id) {
      setAuthPopoverNetwork(null);
    }
  };

  return (
    <div className={styles.socialMediaCard}>
      <span className={styles.socialMediaTitle}>{t('chatsocket.social_media.title')}</span>

      <div className={styles.networksList}>
        {SOCIAL_NETWORKS.map((network) => {
          const isConnected = !!connections?.[network.key];
          const isSelected = selectedNetworks.includes(network.id);

          return (
            <div
              key={network.id}
              className={`${styles.socialOption} ${isSelected ? styles.selected : ''} ${isReadOnly || !isConnected ? styles.locked : ''}`}
              onClick={() => handleNetworkClick(network)}
              style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer' }}
            >
              <div className={styles.socialOptionLeft}>
                {network.icon}
                {t(NETWORK_NAME_KEYS[network.key])}
              </div>

              <div className={styles.socialOptionRight}>
                {isConnected ? (
                  isSelected && !isReadOnly ? (
                    <button
                      type="button"
                      className={styles.checkButton}
                      onClick={(event) => handleDeselectNetwork(event, network)}
                      aria-label={t('chatsocket.social_media.remove', { name: t(NETWORK_NAME_KEYS[network.key]) })}
                    >
                      <svg
                        className={styles.checkIcon}
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20 6L9 17L4 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  ) : (
                    <LockClosed width={20} height={20} />
                  )
                ) : (
                  <LockClosed width={20} height={20} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isConnecting && !usesInstagramOAuthRedirect && (
        <div className={styles.oauthAwaitingPanel}>
          <span className={styles.oauthAwaitingText}>
            {t('chatsocket.social_media.errors.instagram_oauth_awaiting_popup')}
          </span>
          <div className={styles.oauthAwaitingActions}>
            <button
              type="button"
              className={styles.oauthSecondaryBtn}
              onClick={reopenInstagramOAuthWindow}
            >
              {t('chatsocket.social_media.errors.instagram_oauth_reopen_window')}
            </button>
            <button
              type="button"
              className={styles.oauthCancelBtn}
              onClick={cancelInstagramOAuth}
            >
              {t('chatsocket.social_media.errors.instagram_oauth_cancel')}
            </button>
          </div>
        </div>
      )}

      <button
        className={styles.finalizarBtn}
        disabled={isReadOnly || selectedNetworks.length === 0}
        onClick={() => {
          if (isReadOnly) {
            return;
          }

          if (!validateBeforeFinalize()) {
            return;
          }
          onFinalize && onFinalize(buildNetworkPayload());
        }}
      >
        {t('chatsocket.social_media.finalize')}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20 6L9 17L4 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <SocialMediaAuthPopover
        isOpen={!!authPopoverNetwork}
        onClose={() => setAuthPopoverNetwork(null)}
        network={authPopoverNetwork}
        settings={networkSettings}
        facebookPages={facebookPages}
        tiktokCreatorInfo={tiktokCreatorInfo}
        tiktokPrivacyOptions={tiktokPrivacyOptions}
        onUpdateSettings={updateNetworkSettings}
        onSave={handleSaveNetworkSettings}
        isReadOnly={isReadOnly}
      />
    </div>
  );
};
